import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "../../lib/api";
import { Shell } from "./_shell";
import { EmailDot, StatusSelect, ConfirmDialog, ConvertResultDialog } from "./_shared";

export default function Enquiries() {
  const [list, setList] = useState([]);
  const [confirm, setConfirm] = useState(null); // { kind: 'delete'|'convert', enquiry }
  const [busy, setBusy] = useState(false);
  const [convertResult, setConvertResult] = useState(null);

  const load = () => api.get("/enquiries").then(r => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.put(`/enquiries/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const doDelete = async (q) => {
    setBusy(true);
    try { await api.delete(`/enquiries/${q.id}`); toast.success(`Enquiry #${q.inquiry_no ?? ""} deleted`); setConfirm(null); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doConvert = async (q) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/enquiries/${q.id}/convert-to-retailer`);
      toast.success(`Retailer created for ${data.email}`);
      setConfirm(null);
      setConvertResult(data);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); setConfirm(null); }
    finally { setBusy(false); }
  };

  return (
    <Shell active="enquiries">
      <div className="p-5 md:p-10">
        <div className="overline mb-2">Wholesale enquiries</div>
        <h1 className="font-display text-4xl md:text-6xl uppercase leading-none mb-8">Enquiries.</h1>
        <div className="space-y-3" data-testid="enquiries-list">
          {list.map(q => (
            <div key={q.id} className={`border p-5 bg-paper ${["completed","archived","spam"].includes(q.admin_status) ? "border-black/20 opacity-70" : "border-ink"}`} data-testid={`admin-enquiry-${q.id}`}>
              <div className="flex justify-between flex-wrap gap-2 mb-2">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-xl leading-none" data-testid={`enquiry-no-${q.id}`}>#{q.inquiry_no ?? "—"}</span>
                    <span className="font-bold">{q.business_name}</span>
                    {q.converted_to_retailer_id && <span className="chip text-[9px] text-cobalt border-cobalt">Converted to retailer</span>}
                  </div>
                  <div className="text-xs text-black/60 mt-1">{q.contact_name} · {q.phone} · {q.email} · {q.city}</div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <EmailDot sent={q.email_customer_sent} label="Customer" />
                    <EmailDot sent={q.email_business_sent} label="Business" />
                  </div>
                  <StatusSelect value={q.admin_status} onChange={(s) => setStatus(q.id, s)} testid={`enquiry-status-${q.id}`} />
                  <div className="text-xs text-black/60 w-full md:w-auto">{new Date(q.created_at).toLocaleString()}</div>
                </div>
              </div>
              {q.products_of_interest && <div className="mt-2"><span className="overline text-black/50">Products: </span><span className="text-sm">{q.products_of_interest}</span></div>}
              {q.estimated_quantities && <div className="mt-1"><span className="overline text-black/50">Qty: </span><span className="text-sm">{q.estimated_quantities}</span></div>}
              {q.notes && <p className="text-sm mt-2 whitespace-pre-wrap">{q.notes}</p>}
              <div className="mt-4 pt-3 border-t border-black/10 flex flex-wrap gap-2">
                {!q.converted_to_retailer_id && (
                  <button onClick={() => setConfirm({ kind: "convert", enquiry: q })} className="btn-ghost text-[11px] px-3 py-1.5" data-testid={`enquiry-convert-${q.id}`}>
                    → Convert to retailer
                  </button>
                )}
                <button onClick={() => setStatus(q.id, "archived")} className="btn-ghost text-[11px] px-3 py-1.5" data-testid={`enquiry-archive-${q.id}`}>
                  Archive
                </button>
                <button onClick={() => setConfirm({ kind: "delete", enquiry: q })} className="text-[11px] px-3 py-1.5 underline text-soda" data-testid={`enquiry-delete-${q.id}`}>
                  Delete
                </button>
              </div>
            </div>
          ))}
          {list.length === 0 && <div className="border border-ink p-8 text-center text-black/50">No enquiries yet.</div>}
        </div>
      </div>
      <ConfirmDialog
        open={confirm?.kind === "delete"}
        title={`Delete enquiry #${confirm?.enquiry?.inquiry_no ?? ""}?`}
        body={`This will permanently remove the enquiry from ${confirm?.enquiry?.business_name || "this business"}. This action cannot be undone.\n\nUse "Archive" instead if you want to keep it for records.`}
        confirmLabel="Delete permanently" danger busy={busy}
        onConfirm={() => doDelete(confirm.enquiry)} onCancel={() => setConfirm(null)}
        testid="enquiry-delete-dialog"
      />
      <ConfirmDialog
        open={confirm?.kind === "convert"}
        title="Convert to retailer account?"
        body={`This will create a pending retailer account for ${confirm?.enquiry?.email || "this contact"} pre-filled from the enquiry.\n\nYou'll then approve them normally from the Retailers page. A temporary password will be shown once — copy it and share with the retailer.`}
        confirmLabel="Create retailer" busy={busy}
        onConfirm={() => doConvert(confirm.enquiry)} onCancel={() => setConfirm(null)}
        testid="enquiry-convert-dialog"
      />
      <ConvertResultDialog result={convertResult} onClose={() => setConvertResult(null)} />
    </Shell>
  );
}
